import React from 'react';
import {
    DollarSign, TrendingUp, Clock, Plane, ArrowUpRight,
    Building2, Coins, PiggyBank, Target
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';

// ============================================
// TYPES for ROI Economic Impact
// ============================================

export interface EconomicImpact {
    annual_delay_savings: number;
    annual_turnaround_savings: number;
    additional_capacity_value: number;
    total_annual_impact: number;
}

interface ROIPanelProps {
    economicImpact: EconomicImpact | null;
    projectName?: string;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

const formatCurrency = (value: number | undefined | null): string => {
    const n = value ?? 0;
    if (Math.abs(n) >= 1_000_000) {
        return `$${(n / 1_000_000).toFixed(1)}M`;
    } else if (Math.abs(n) >= 1_000) {
        return `$${(n / 1_000).toFixed(0)}K`;
    }
    return `$${n.toFixed(0)}`;
};

const formatLargeCurrency = (value: number | undefined | null): string => {
    const n = value ?? 0;
    if (Math.abs(n) >= 1_000_000) {
        return `${(n / 1_000_000).toFixed(2)}M`;
    } else if (Math.abs(n) >= 1_000) {
        return `${(n / 1_000).toFixed(0)}K`;
    }
    return n.toFixed(0);
};

// ============================================
// COMPONENT
// ============================================

export const ROIPanel: React.FC<ROIPanelProps> = ({ economicImpact, projectName }) => {
    const { theme } = useTheme();
    const { language } = useLanguage();
    const isDark = theme === 'dark';

    const textMain = 'text-[var(--text-primary)]';
    const textSub = 'text-[var(--text-secondary)]';
    const cardBg = isDark ? 'bg-white/80' : 'bg-white';
    const borderColor = 'border-[var(--border)]';

    if (!economicImpact) {
        return (
            <div className={`rounded-xl border p-6 ${cardBg} ${borderColor}`}>
                <p className={`text-center ${textSub}`}>
                    {language === 'es'
                        ? 'No hay datos de impacto económico disponibles.'
                        : 'No economic impact data available.'}
                </p>
            </div>
        );
    }

    const metrics = [
        {
            key: 'delay_savings',
            label: language === 'es' ? 'Ahorros en Demoras' : 'Delay Savings',
            value: economicImpact.annual_delay_savings,
            icon: Clock,
            color: 'text-blue-500',
            bgColor: isDark ? 'bg-blue-500/10' : 'bg-blue-50',
            description: language === 'es'
                ? 'Reducción de costos por demoras de salida'
                : 'Cost reduction from departure delays'
        },
        {
            key: 'turnaround_savings',
            label: language === 'es' ? 'Ahorros en Turnaround' : 'Turnaround Savings',
            value: economicImpact.annual_turnaround_savings,
            icon: Plane,
            color: 'text-emerald-500',
            bgColor: isDark ? 'bg-emerald-500/10' : 'bg-emerald-50',
            description: language === 'es'
                ? 'Reducción de tiempo en tierra'
                : 'Ground time reduction'
        },
        {
            key: 'capacity_value',
            label: language === 'es' ? 'Valor de Capacidad' : 'Capacity Value',
            value: economicImpact.additional_capacity_value,
            icon: Building2,
            color: 'text-purple-500',
            bgColor: isDark ? 'bg-purple-500/10' : 'bg-purple-50',
            description: language === 'es'
                ? 'Valor de slots adicionales disponibles'
                : 'Value of additional available slots'
        }
    ];

    const totalImpact = economicImpact.total_annual_impact;
    const isPositive = totalImpact > 0;

    return (
        <div className={`rounded-xl border overflow-hidden ${cardBg} ${borderColor}`}>
            {/* Header */}
            <div className={`p-4 border-b ${borderColor} ${isDark ? 'bg-gradient-to-r from-[#1C1C1C] to-[#00254d]' : 'bg-gradient-to-r from-gray-50 to-gray-100'}`}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${isDark ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}>
                            <DollarSign className="w-5 h-5 text-emerald-500" />
                        </div>
                        <div>
                            <h3 className={`text-lg font-bold ${textMain}`}>
                                {language === 'es' ? 'Impacto Económico Anual' : 'Annual Economic Impact'}
                            </h3>
                            <p className={`text-sm ${textSub}`}>
                                {language === 'es'
                                    ? 'ROI estimado basado en benchmarks de la industria'
                                    : 'Estimated ROI based on industry benchmarks'}
                            </p>
                        </div>
                    </div>

                    {/* Total Impact Badge */}
                    <div className={`px-4 py-2 rounded-lg ${isPositive
                        ? (isDark ? 'bg-emerald-500/20 border border-emerald-500/30' : 'bg-emerald-100 border border-emerald-200')
                        : (isDark ? 'bg-red-500/20 border border-red-500/30' : 'bg-red-100 border border-red-200')
                        }`}>
                        <div className="flex items-center gap-2">
                            {isPositive ? (
                                <TrendingUp className="w-5 h-5 text-emerald-500" />
                            ) : (
                                <TrendingUp className="w-5 h-5 text-red-500 rotate-180" />
                            )}
                            <span className={`text-2xl font-black ${isPositive ? 'text-emerald-500' : 'text-red-500'}`}>
                                {formatCurrency(totalImpact)}
                            </span>
                            <span className={`text-sm ${textSub}`}>/año</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Metrics Grid */}
            <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {metrics.map(metric => (
                        <div
                            key={metric.key}
                            className={`p-4 rounded-xl border ${borderColor} ${metric.bgColor} transition-all hover:scale-[1.02]`}
                        >
                            <div className="flex items-start justify-between mb-3">
                                <div className={`p-2 rounded-lg ${'bg-[var(--surface-raised)]'}`}>
                                    <metric.icon className={`w-5 h-5 ${metric.color}`} />
                                </div>
                                <ArrowUpRight className={`w-4 h-4 ${metric.color}`} />
                            </div>

                            <div className="space-y-1">
                                <p className={`text-sm font-medium ${textSub}`}>
                                    {metric.label}
                                </p>
                                <p className={`text-2xl font-bold ${textMain}`}>
                                    ${formatLargeCurrency(metric.value)}
                                </p>
                                <p className={`text-xs ${textSub}`}>
                                    {metric.description}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Calculation Details */}
            <div className={`px-4 pb-4`}>
                <div className={`p-3 rounded-lg ${'bg-[var(--surface-raised)]'} border ${borderColor}`}>
                    <div className="flex items-start gap-2">
                        <Target className={`w-4 h-4 mt-0.5 ${textSub}`} />
                        <div className={`text-xs ${textSub}`}>
                            <p className="font-medium mb-1">
                                {language === 'es' ? 'Metodología de Cálculo:' : 'Calculation Methodology:'}
                            </p>
                            <ul className="space-y-0.5 ml-2">
                                <li>• {language === 'es'
                                    ? 'Ahorros en demoras: ΔDelay × vuelos/año × $100/min'
                                    : 'Delay savings: ΔDelay × flights/year × $100/min'}</li>
                                <li>• {language === 'es'
                                    ? 'Ahorros turnaround: ΔTurnaround × vuelos/año × $50/min'
                                    : 'Turnaround savings: ΔTurnaround × flights/year × $50/min'}</li>
                                <li>• {language === 'es'
                                    ? 'Valor capacidad: ΔSlots × $5,000/slot'
                                    : 'Capacity value: ΔSlots × $5,000/slot'}</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className={`px-4 py-3 border-t ${borderColor} ${isDark ? 'bg-emerald-900/20' : 'bg-emerald-50'}`}>
                <div className="flex items-center gap-2">
                    <PiggyBank className="w-4 h-4 text-emerald-500" />
                    <span className={`text-sm font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
                        {language === 'es'
                            ? `Impacto total estimado: ${formatCurrency(totalImpact)} anuales`
                            : `Estimated total impact: ${formatCurrency(totalImpact)} annually`}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default ROIPanel;
